import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { get } from 'lodash';
import { setPush } from 'store/actions/app';
import RealizationList from '../../components/realization-list';
import DetailsContainer from '../../containers/details-container';
import AppPageMain from '../../modules/app-page/app-page--main';
import LoadDocBtn from '../../modules/load-doc-btn';
import PageHeader from '../../modules/page-header';
import { AgreementInfoResponse } from '../../services/api/dto/shared';
import { useAppDispatch } from '../../store';
import { getIssue, getUcpDoc } from '../../store/actions/operations-sign';
import { RootState } from '../../types';
import currencyFormat from '../../utils/currencyFormat';
import { downloadFile, getPdfFromBase64 } from '../../utils/files/downloadFile';
import { getIssueBadge } from '../../utils/getStatusBadge';
import getWordDecl from '../../utils/getWordDecl';
import { getFormatDate } from '../../utils/utils';

const HybridInfoPage: FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const isHistory = useMemo(() => history.location.state?.isHistory, [history]);
  const id = useMemo(() => history.location.state?.id, [history]);
  const issue = useSelector(
    (state: RootState) => state.operationsSign.data?.issue
  );

  useEffect(() => {
    id && dispatch(getIssue({ id }));
  }, [id]);

  const downloadDoc = useCallback(
    (sig: boolean) => {
      if (issue?.decision)
        dispatch(
          getUcpDoc({
            id: issue?.decision.id,
            type: sig ? 'SIGNED_DECISION' : 'DECISION',
            contentType: 'application/pdf',
          })
        )
          .then((res) => {
            const resultBlob = getPdfFromBase64(
              (res.payload as { doc: string; hash: string }).doc
            );
            downloadFile(
              resultBlob,
              `Решение о выпуске №${issue?.decision.id}.pdf${
                sig ? '.sig' : ''
              }`,
              'application/pdf'
            );
          })
          .catch(() =>
            dispatch(
              setPush({
                message: 'Ошибка при загрузке файла',
                type: 'error',
              })
            )
          );
    },
    [issue]
  );

  const decisionDocs = useMemo(() => {
    const docs = [];

    if (issue?.decision)
      docs.push(
        <LoadDocBtn
          title={`Решение о выпуске №${issue?.decision.id}`}
          sig
          onSpecDownload={() => downloadDoc(false)}
          onSpecDownloadSig={() => downloadDoc(true)}
        />
      );
    return docs;
  }, [issue?.decision]);

  const detailsListLeft = useMemo(
    () => [
      [
        {
          title: 'Поставщик:',
          value: get(issue, 'issuer', '-'),
        },
        {
          title: 'ИНН:',
          value: get(issue, 'issuerTIN', '-'),
        },
      ],
      [
        {
          title: 'Заказчик:',
          value: get(issue, 'recipient', '-'),
        },
        {
          title: 'ИНН:',
          value: get(issue, 'recipientTIN', '-'),
        },
      ],
      [
        {
          title: 'Дата создания:',
          value: getFormatDate(
            get(issue, 'initialDate', new Date()),
            undefined,
            false
          ),
        },
        {
          title: 'Дата исполнения:',
          value: get(issue, 'finalizedDate')
            ? getFormatDate(
                get(issue, 'finalizedDate', new Date()),
                undefined,
                false
              )
            : '-',
        },
      ],
    ],
    [issue]
  );

  const agreementLinks = useMemo(() => issue?.issues || [], [issue]);

  return (
    <AppPageMain>
      <div className="container">
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <PageHeader
              items={[
                {
                  link: isHistory ? '/history' : '/assets',
                  title: isHistory ? 'Операции' : 'Активы',
                },
                { link: isHistory ? '/history' : '#', title: 'Выпуск' },
              ]}
              iconType={isHistory ? 'history' : 'assets'}
              tabName={isHistory && 'issue'}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <DetailsContainer
              title="Выпуск товарного требования"
              onClose={() => history.push(isHistory ? '/history' : '/assets')}
              infoTitle={{
                steps: !isHistory
                  ? [
                      { title: 'Выбор суммы' },
                      { title: 'Заявка' },
                      { title: 'Статус заявки', active: true },
                    ]
                  : undefined,
                title: 'Заявка:',
                description: `Выпуск товарного требования:
                ${agreementLinks.length} ${getWordDecl(agreementLinks.length, [
                  'реализация',
                  'реализации',
                  'реализаций',
                ])} на сумму ${currencyFormat(
                  Number(get(issue, 'sumAmount', 0))
                )}`,
                status: getIssueBadge(get(issue, 'status', '')),
              }}
              leftContent={{
                title: 'Детали операции:',
                docTitle: 'Подписанные документы:',
                detailsList: detailsListLeft,
                docs: decisionDocs,
              }}
              rightTopComponent={
                agreementLinks.length ? (
                  <RealizationList
                    title="Состав требования:"
                    list={agreementLinks.map(
                      (realization: AgreementInfoResponse) => ({
                        refNumber: realization.refNumber,
                        sum: currencyFormat(realization.value),
                        link: `/orders/${realization.orderId}/items/${realization.orderItemId}/realizations`,
                      })
                    )}
                  />
                ) : undefined
              }
            />
          </div>
        </div>
      </div>
    </AppPageMain>
  );
};

export default HybridInfoPage;
